/**
 * Mock data for Orçamentos (Budget Phases) page
 *
 * Simulates API responses with realistic budget data for development and testing
 */

import {
  OrcamentosPageData,
  BudgetPhase,
  PhaseChartData,
  PhaseColor,
} from "@/types/orcamentos.interface";

/**
 * Map phase names to their visual color identifiers
 */
export function getPhaseColor(phaseName: string): PhaseColor {
  const phaseColorMap: Record<string, PhaseColor> = {
    "Desenvolvimento do Projeto": "purple",
    "Pré-Produção": "purple",
    "Produção": "orange",
    "Pós-Produção": "orange",
    "Despesas Administrativas": "teal",
    "Tributos e Taxas": "teal",
    "Gerenciamento": "teal",
  };

  return phaseColorMap[phaseName] || "purple";
}

/**
 * Mock budget phases data with realistic values and 3-level hierarchy
 */
const mockBudgetPhases: BudgetPhase[] = [
  {
    id: "1",
    phaseName: "Desenvolvimento do Projeto",
    orcamentoExecutado: 49587.0,
    orcamentoAprovado: 500000.0,
    percentual: 125,
    topics: [
      {
        id: "1-1",
        name: "Banco de Dados",
        orcamentoExecutado: 25000.0,
        orcamentoAprovado: 20000.0,
        percentual: 125,
        lineItems: [
          {
            id: "1-1-1",
            name: "Jpp Empresa",
            orcamentoPrevisto: 20000.0,
            value: 25000.0,
            percentual: 125,
            lastUpdate: "20/06/2025",
          },
          {
            id: "1-1-2",
            name: "Jpp Empresa",
            orcamentoPrevisto: 20000.0,
            value: 25000.0,
            percentual: 125,
            lastUpdate: "20/06/2025",
          },
          {
            id: "1-1-3",
            name: "Jpp Empresa",
            orcamentoPrevisto: 20000.0,
            value: 25000.0,
            percentual: 125,
            lastUpdate: "20/06/2025",
          },
        ],
      },
      {
        id: "1-2",
        name: "Pesquisa",
        orcamentoExecutado: 25000.0,
        orcamentoAprovado: 20000.0,
        percentual: 125,
        lineItems: [
          {
            id: "1-2-1",
            name: "Jpp Empresa",
            orcamentoPrevisto: 20000.0,
            value: 25000.0,
            percentual: 125,
            lastUpdate: "20/06/2025",
          },
        ],
      },
    ],
    iniciadoEm: "2025-06-26",
    atualizadoEm: "2025-06-26",
  },
  {
    id: "2",
    phaseName: "Pré-Produção",
    orcamentoExecutado: 89350.0,
    orcamentoAprovado: 300000.0,
    percentual: 125,
    topics: [
      {
        id: "2-1",
        name: "Equipe",
        orcamentoExecutado: 25000.0,
        orcamentoAprovado: 20000.0,
        percentual: 125,
        lineItems: [
          {
            id: "2-1-1",
            name: "Jpp Empresa",
            orcamentoPrevisto: 20000.0,
            value: 25000.0,
            percentual: 125,
            lastUpdate: "20/06/2025",
          },
        ],
      },
      {
        id: "2-2",
        name: "Alimentação",
        orcamentoExecutado: 25000.0,
        orcamentoAprovado: 20000.0,
        percentual: 125,
        lineItems: [],
      },
    ],
    iniciadoEm: "2025-06-20",
    atualizadoEm: "2025-06-26",
  },
  {
    id: "3",
    phaseName: "Produção",
    orcamentoExecutado: 250000.0,
    orcamentoAprovado: 800000.0,
    percentual: 125,
    topics: [
      {
        id: "3-1",
        name: "Equipe de Produção",
        orcamentoExecutado: 120000.0,
        orcamentoAprovado: 140000.0,
        percentual: 86,
        lineItems: [
          {
            id: "3-1-1",
            name: "Jpp Empresa",
            orcamentoPrevisto: 140000.0,
            value: 120000.0,
            percentual: 86,
            lastUpdate: "20/06/2025",
          },
        ],
      },
      {
        id: "3-2",
        name: "Atores e Elenco",
        orcamentoExecutado: 80000.0,
        orcamentoAprovado: 95000.0,
        percentual: 84,
        lineItems: [
          {
            id: "3-2-1",
            name: "Jpp Empresa",
            orcamentoPrevisto: 95000.0,
            value: 80000.0,
            percentual: 84,
            lastUpdate: "20/06/2025",
          },
        ],
      },
    ],
    iniciadoEm: "2025-06-10",
    atualizadoEm: "2025-06-26",
  },
  {
    id: "4",
    phaseName: "Pós-Produção",
    orcamentoExecutado: 125400.0,
    orcamentoAprovado: 350000.0,
    percentual: 125,
    topics: [
      {
        id: "4-1",
        name: "Edição de Vídeo",
        orcamentoExecutado: 60000.0,
        orcamentoAprovado: 70000.0,
        percentual: 86,
        lineItems: [
          {
            id: "4-1-1",
            name: "Jpp Empresa",
            orcamentoPrevisto: 70000.0,
            value: 60000.0,
            percentual: 86,
            lastUpdate: "20/06/2025",
          },
        ],
      },
    ],
    iniciadoEm: "2025-06-15",
    atualizadoEm: "2025-06-25",
  },
  {
    id: "5",
    phaseName: "Despesas Administrativas",
    orcamentoExecutado: 35600.0,
    orcamentoAprovado: 150000.0,
    percentual: 125,
    topics: [
      {
        id: "5-1",
        name: "Pessoal Administrativo",
        orcamentoExecutado: 20000.0,
        orcamentoAprovado: 24000.0,
        percentual: 83,
        lineItems: [
          {
            id: "5-1-1",
            name: "Jpp Empresa",
            orcamentoPrevisto: 24000.0,
            value: 20000.0,
            percentual: 83,
            lastUpdate: "20/06/2025",
          },
        ],
      },
    ],
    iniciadoEm: "2025-06-01",
    atualizadoEm: "2025-06-26",
  },
  {
    id: "6",
    phaseName: "Tributos e Taxas",
    orcamentoExecutado: 22500.0,
    orcamentoAprovado: 100000.0,
    percentual: 125,
    topics: [
      {
        id: "6-1",
        name: "ISS",
        orcamentoExecutado: 12000.0,
        orcamentoAprovado: 14000.0,
        percentual: 86,
        lineItems: [
          {
            id: "6-1-1",
            name: "Jpp Empresa",
            orcamentoPrevisto: 14000.0,
            value: 12000.0,
            percentual: 86,
            lastUpdate: "20/06/2025",
          },
        ],
      },
    ],
    iniciadoEm: "2025-06-01",
    atualizadoEm: "2025-06-26",
  },
  {
    id: "7",
    phaseName: "Gerenciamento",
    orcamentoExecutado: 18750.0,
    orcamentoAprovado: 75000.0,
    percentual: 125,
    topics: [
      {
        id: "7-1",
        name: "Gerente de Projeto",
        orcamentoExecutado: 12000.0,
        orcamentoAprovado: 14000.0,
        percentual: 86,
        lineItems: [
          {
            id: "7-1-1",
            name: "Jpp Empresa",
            orcamentoPrevisto: 14000.0,
            value: 12000.0,
            percentual: 86,
            lastUpdate: "20/06/2025",
          },
        ],
      },
    ],
    iniciadoEm: "2025-06-01",
    atualizadoEm: "2025-06-26",
  },
];

/**
 * Mock chart data for horizontal phase comparison
 */
const mockChartData: PhaseChartData[] = [
  { name: "Desenvolvimento do Projeto", value: 49587 },
  { name: "Pré-Produção", value: 89350 },
  { name: "Produção", value: 250000 },
  { name: "Pós-Produção", value: 125400 },
  { name: "Despesas Administrativas", value: 35600 },
  { name: "Tributos e Taxas", value: 22500 },
  { name: "Gerenciamento", value: 18750 },
];

/**
 * Mock project list
 */
const mockProjects = [
  { id: "proj-001", name: "Projeto: Mai Editar" },
  { id: "proj-002", name: "Documentário Amazônia" },
  { id: "proj-003", name: "Série Web Drama" },
  { id: "proj-004", name: "Curta Metragem Ficção" },
];

/**
 * Complete mock data for Orçamentos page
 */
export const mockOrcamentosData: OrcamentosPageData = {
  availableProjects: mockProjects,
  selectedProject: mockProjects[0],
  budgetPhases: mockBudgetPhases,
  chartData: mockChartData,
};
